import React, { useState } from "react";
import { useMantineTheme } from "@mantine/core";
import { useIntl } from "react-intl";
import { useRouter } from "next/router";
import { Section } from "@/components/common/section/Section";
import HydratedMediaQuery from "@/utils/hydratedMediaQuery";
import { getBreakpoint } from "@/utils/mantine";
import { CtaButton } from "@/components/common/ctaButton/CtaButton";
import { routes } from "@/routes/routes";
import CompanySearchInput from "@/components/common/companySearchInput/CompanySearchInput";
import { CompanyProposition } from "@/components/common/companySearchInput/hooks/useFetchAutocompleteCompanies";
import { formatStrapiEntity, StrapiEntity, StrapiImage } from "@/utils/strapi";
import styles from "./SearchCompany.module.scss";

type SearchCompanyProps = {
  title: string;
  desktopImage: StrapiEntity<StrapiImage> | { data: null };
  mobileImage: StrapiEntity<StrapiImage> | { data: null };
};

const SearchCompany = ({
  title,
  desktopImage,
  mobileImage,
}: SearchCompanyProps) => {
  const theme = useMantineTheme();
  const isTabletOrDesktop = HydratedMediaQuery(
    getBreakpoint(theme.breakpoints.md)
  );
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyProposition | null>(null);
  const intl = useIntl();
  const router = useRouter();

  function cleanString(input: string): string {
    return input.replace(/[^a-zA-Z0-9]/g, "");
  }

  const onClick = () => {
    const isExcludedNafCode = ["4941A", "4941B"].includes(
      cleanString(selectedCompany?.nafCode ?? "")
    );
    if (isExcludedNafCode) {
      return router.push(routes.solutionPro);
    }

    const queryParams = {
      siret: selectedCompany?.siret,
      businessName: selectedCompany?.businessName,
      nafCode: selectedCompany?.nafCode,
      street: selectedCompany?.address.street,
      rawAddress: selectedCompany?.address.rawAddress,
      zipCode: selectedCompany?.address.zipCode,
      city: selectedCompany?.address.city,
      country: selectedCompany?.address.country,
    };

    return router.push({
      pathname: routes.loyaltyProgramInscription,
      query: queryParams,
    });
  };

  let backgroundImage: StrapiImage | null = null;

  if (isTabletOrDesktop) {
    backgroundImage =
      desktopImage.data !== null ? formatStrapiEntity(desktopImage) : null;
  } else {
    backgroundImage =
      mobileImage.data !== null ? formatStrapiEntity(mobileImage) : null;
  }

  return (
    <Section>
      <div
        className={styles.SearchCompany}
        style={{
          backgroundImage: backgroundImage
            ? `url(${backgroundImage.url})`
            : undefined,
          backgroundColor: backgroundImage ? undefined : "#062e5e",
        }}
      >
        <div className={styles.searchSection}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.input}>
            <CompanySearchInput
              placeholder={intl.formatMessage({ id: "companyNameOrSiret" })}
              onSelectCompany={setSelectedCompany}
              selectedCompany={selectedCompany}
            />
          </div>
          <CtaButton
            label={intl.formatMessage({ id: "validate" })}
            onClick={onClick}
            overloadContainerStyle={styles.alignStart}
            isDisabled={selectedCompany === null}
          />
        </div>
      </div>
    </Section>
  );
};

export default SearchCompany;
